import { PaymentProcessor } from '@mr-yum/payment-proto/node/payment/v1/payment_processor'
import { IntlShape } from 'react-intl'
import z from 'zod'

import { GuestVenue, OrderingType, VenueFieldRequirement } from '@/gql/graphql'

import { PaymentMethodKey } from './hooks/usePaymentFormMethods'

/*
  These are the payment methods where we access paymentMethod.value in order to process transaction.
  These should ideally be static values that don't change at runtime after we first load data and
  set initial form values.
*/
export const paymentMethodKeysRequiringValue = [
  PaymentMethodKey.StripeExistingCard,
  PaymentMethodKey.TabExisting,
  PaymentMethodKey.PaystackExistingCard,
]

export const paymentMethodKeysRequiringEmail = [PaymentMethodKey.TabNew]

//TO DO: add translation.

export const paymentMethodSchema = z.object({
  paymentMethod: z
    .object({
      key: z.nativeEnum(PaymentMethodKey),
      value: z.string().nullable(),
      tracking: z.string(),
      processorType: z.nativeEnum(PaymentProcessor, {
        invalid_type_error: 'No payment type selected',
      }),
    })
    .superRefine((data, ctx) => {
      if (paymentMethodKeysRequiringValue.includes(data.key) && !data.value) {
        ctx.addIssue({
          code: 'custom',
          path: ['value'],
          message: 'Payment method value is required',
        })
      }
    }),
})

export function getPaymentSchema(
  {
    orderingType,
    hasOptions,
    emailFieldRequirement,
    coverCountFieldRequirement,
    venue,
    showEmailFieldWhenDineIn,
    isNewGroupOrder,
    isEonxEnv,
  }: {
    venue: Pick<GuestVenue, 'ageRestrictionText'>
    hasOptions: boolean
    orderingType: OrderingType
    emailFieldRequirement: VenueFieldRequirement
    coverCountFieldRequirement: VenueFieldRequirement
    showEmailFieldWhenDineIn: boolean
    isNewGroupOrder?: boolean
    isEonxEnv: boolean | null
  },
  { formatMessage }: IntlShape,
) {
  const coverCountSchema = z.preprocess(
    (a) => {
      if (typeof a === 'number') {
        return a
      }
      if (a === null || a === '') {
        return NaN
      }
      return parseInt(z.string().parse(a), 10)
    },
    z
      .number({
        invalid_type_error: formatMessage({
          id: 'GaaUEa',
          defaultMessage: 'Requires a number between 1 and 20',
        }),
        required_error: formatMessage({
          id: 'mG+rlo',
          defaultMessage: 'Cover count is required',
        }),
      })
      .int()
      .positive()
      .min(
        1,
        formatMessage({
          id: 'GaaUEa',
          defaultMessage: 'Requires a number between 1 and 20',
        }),
      )
      .max(
        20,
        formatMessage({
          id: 'GaaUEa',
          defaultMessage: 'Requires a number between 1 and 20',
        }),
      ),
  )

  const emailSchema = z
    .string({
      required_error: formatMessage({
        id: 'gtleS5',
        defaultMessage: 'Please provide your email address',
      }),
    })
    .email(
      formatMessage({
        id: 'qDmele',
        defaultMessage: 'Email must be valid',
      }),
    )

  const optionalEmailSchema = emailSchema
    .optional()
    .nullable()
    .or(z.literal(''))

  const shouldTransformOptionalEmailToNull =
    isEonxEnv ||
    (emailFieldRequirement === VenueFieldRequirement.Hidden &&
      !showEmailFieldWhenDineIn)

  const optionalCoverCountSchema = coverCountSchema
    .optional()
    .nullable()
    .or(z.literal(''))

  return z
    .object({
      name: z
        .string({
          required_error: formatMessage({
            defaultMessage: 'Your name is required',
            id: '1KxtyA',
          }),
        })
        .trim()
        .min(
          1,
          formatMessage({
            defaultMessage: 'Your name is required',
            id: '1KxtyA',
          }),
        ),
      // There's a post validation step here
      email: shouldTransformOptionalEmailToNull
        ? optionalEmailSchema.transform(() => null)
        : emailFieldRequirement === VenueFieldRequirement.Required
          ? emailSchema
          : optionalEmailSchema,
      coverCount:
        coverCountFieldRequirement === VenueFieldRequirement.Required
          ? coverCountSchema
          : coverCountFieldRequirement === VenueFieldRequirement.Hidden
            ? optionalCoverCountSchema.transform(() => null)
            : optionalCoverCountSchema,

      deliveryOption:
        orderingType &&
        [OrderingType.InVenue, OrderingType.Delivery].includes(orderingType) &&
        hasOptions
          ? z
              .string({
                required_error: formatMessage({
                  id: '0xNxPW',
                  defaultMessage: 'Please choose an option',
                }),
              })
              .nullable()
          : z.string().nullable().optional(),
      pickupOption:
        orderingType &&
        ![OrderingType.InVenue, OrderingType.Delivery].includes(orderingType) &&
        hasOptions
          ? z
              .string({
                required_error: formatMessage({
                  id: '0xNxPW',
                  defaultMessage: 'Please choose an option',
                }),
              })
              .nullable()
          : z.string().nullable().optional(),
      isOver18: venue?.ageRestrictionText
        ? z.literal<boolean>(true, {
            errorMap: () => ({
              message: formatMessage({
                id: 'Q5SxGZ',
                defaultMessage: 'Confirmation is needed to continue',
              }),
            }),
          })
        : z.boolean().optional().nullable(),
      marketingUserOptIn: z.boolean().nullable(),
      marketingUserOptInVisible: z.boolean().nullable(),
      savePaymentDetails: z.boolean().nullable().optional(),
      reApplyVenueWide: z.boolean().default(true).optional(),
    })
    .merge(paymentMethodSchema)
    .superRefine((data, ctx) => {
      const { paymentMethod, marketingUserOptIn, marketingUserOptInVisible } =
        data

      /*
        Email is required if
        - not eonxEnvironment
        - marketing opt in
        - new tab payment method
        - venue field requirement is required
      */

      const requireEmailField =
        emailFieldRequirement !== VenueFieldRequirement.Hidden &&
        paymentMethodKeysRequiringEmail.includes(paymentMethod.key)

      const marketingOptIn = marketingUserOptIn && marketingUserOptInVisible

      // Double check validation rules
      const isEmailRequired =
        !isEonxEnv && (requireEmailField || marketingOptIn || isNewGroupOrder)

      if (isEmailRequired) {
        const result = emailSchema.safeParse(data.email)

        if (!result.success) {
          result.error.issues.forEach(({ message }) => {
            ctx.addIssue({
              code: 'custom',
              path: ['email'],
              message,
            })
          })
        }
      }
    })
}

export type PaymentValues = z.infer<ReturnType<typeof getPaymentSchema>>

export type UserValues = Pick<PaymentValues, 'name' | 'email'>
